import styled from 'styled-components'

import { askYogiTokens as t } from 'features/ask-yogi/styles/tokens'

/**
 * Root wrapper for a rendered assistant message. Owns the global typescale
 * (headings, body, lists), spacing rhythm between sibling blocks, and the
 * compact-mode variant for the Anywhere widget.
 */
export const MessageContentWrapper = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? t.spacing.lg : t.spacing.md)};
  width: 100%;
  color: ${t.color.text};
  font-size: ${(props) =>
    props.$compact ? t.fontSizeCompact.body : t.fontSize.body};
  line-height: ${t.lineHeight.body};
  letter-spacing: ${t.letterSpacing.body};

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: ${(props) => (props.$compact ? t.spacing.md : t.spacing.lg)};
    margin-bottom: 0;
    font-weight: ${t.fontWeight.heading};
    line-height: ${t.lineHeight.heading};
    letter-spacing: ${t.letterSpacing.heading};
    color: ${t.color.text};
  }

  h1 {
    font-size: ${(props) =>
      props.$compact ? t.fontSizeCompact.title : t.fontSize.title};
  }
  h2 {
    font-size: ${(props) =>
      props.$compact ? t.fontSizeCompact.section : t.fontSize.section};
  }
  h3 {
    font-size: ${(props) =>
      props.$compact ? t.fontSizeCompact.subsection : t.fontSize.subsection};
  }
  h4,
  h5,
  h6 {
    font-size: ${(props) =>
      props.$compact ? t.fontSizeCompact.detail : t.fontSize.detail};
  }

  p {
    font-size: ${(props) =>
      props.$compact ? t.fontSizeCompact.body : t.fontSize.body};
    line-height: ${t.lineHeight.body};
    margin: 0;
  }

  > *:first-child {
    margin-top: 0;
  }

  ul,
  ol {
    padding-left: 1.5em;
    margin-top: 0;
    margin-bottom: 0;
  }

  hr {
    border: none;
    border-top: 1px solid ${t.color.divider};
    /* Minimal margin so it doesn't compound with the wrapper gap and the
       next heading's section-break margin. */
    margin: ${t.spacing.sm} 0;
  }

  code {
    background-color: ${t.color.codeBg};
    padding: 0.15em 0.35em;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.92em;
  }

  pre {
    background-color: ${t.color.codeBg};
    padding: ${t.spacing.sm};
    border-radius: 6px;
    overflow-x: auto;
    margin: 0;
    white-space: pre-wrap;
  }

  blockquote {
    border-left: 3px solid ${t.color.quoteBorder};
    padding-left: ${t.spacing.sm};
    margin: 0 0 0 ${t.spacing.sm};
    color: ${t.color.quoteText};
  }

  a {
    color: ${t.color.link};
    text-decoration: underline;
  }

  strong {
    font-weight: ${t.fontWeight.emphasis};
  }
`

export const TableContainer = styled.div<{ $compact?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${t.spacing.md};
  /* Dense block buffer, layered on top of the wrapper's gap. */
  margin: ${t.spacing.sm} 0;

  tbody tr:nth-child(even) {
    background-color: ${t.color.tableRowAlt};
  }
`

export const TableScrollWrapper = styled.div<{ $compact?: boolean }>`
  flex: 1;
  width: 100%;
  overflow-x: ${(props) => (props.$compact ? 'hidden' : 'auto')};
  scrollbar-width: thin;
`

/**
 * Markdown table styling. Headers stay left-aligned for a uniform top edge;
 * numeric body cells get `data-align="right"` for right-align + tabular-nums
 * + nowrap (set by MessageContent's column-alignment detection).
 */
export const StyledTable = styled.table<{ $compact?: boolean }>`
  border-collapse: collapse;
  width: 100%;
  /* Compact (sidebar/widget) mode fixes column widths instead of growing the
     table to fit content, so the table never forces TableScrollWrapper's
     overflow-x — see the cell comment below for how that interacts with
     overflow-wrap. */
  table-layout: ${(props) => (props.$compact ? 'fixed' : 'auto')};
  font-size: ${(props) =>
    props.$compact ? t.fontSize.caption : t.fontSize.table};
  line-height: ${t.lineHeight.dense};

  th,
  td {
    border: 1px solid ${t.color.tableBorder};
    padding: ${(props) =>
      props.$compact
        ? `${t.spacing.xs} ${t.spacing.sm}`
        : `${t.spacing.sm} ${t.spacing.md}`};
    text-align: left;
    /* break-word (not anywhere) floors a column's min-content width at its
       widest word, so auto table-layout can't compress a column narrower
       than that word — it grows the table and TableScrollWrapper's
       overflow-x scrolls instead. In compact mode, table-layout: fixed
       above takes column width out of content's hands entirely (columns are
       distributed evenly regardless of content), so break-word's job there
       is simpler: wrap a long word within that already-fixed width rather
       than floor a minimum. */
    overflow-wrap: break-word;
    word-break: normal;
  }

  th {
    background-color: ${t.color.tableHeaderBg};
    font-weight: ${t.fontWeight.heading};
    color: ${t.color.text};
  }

  td[data-align='right'] {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`

/**
 * Asset caption tier — shared by chart and table titles. Sized below h3 so
 * a section heading always outranks the asset it introduces. Use for any
 * "label that belongs to a visualization" (not for section breaks).
 */
export const AssetCaption = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.spacing.sm};
  font-size: ${t.fontSize.table};
  font-weight: ${t.fontWeight.body};
  line-height: ${t.lineHeight.heading};
  letter-spacing: ${t.letterSpacing.heading};
  color: ${t.color.muted};
`

// Shared Ask Yogi artifact-toolbar button. Used by Chart.tsx and TableToolbar.tsx
// so the copy / filter / export affordances read the same across artifact types.
export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  cursor: pointer;
  &:hover {
    background: #f3f4f6;
  }
`

/**
 * Caption row for a rendered markdown table. Three slots: title (left, flex),
 * filter (middle-right, optional), toolbar (right-most). Shares the caption
 * typescale with `AssetCaption` but replaces its simple flex layout with
 * three explicit slots so the copy button can anchor right regardless of
 * whether a title or filter icon is present.
 */
// Extends AssetCaption so table title styling can't drift from chart title
// styling. A future ArtifactToolbar extraction collapses this into AssetCaption
// directly.
export const TableCaptionRow = styled(AssetCaption)``

export const TableCaptionTitle = styled.div`
  flex: 1;
  min-width: 0;
`

export const TableCaptionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`

export const StyledUl = styled.ul<{ $compact?: boolean }>`
  font-size: ${(props) =>
    props.$compact ? t.fontSizeCompact.body : t.fontSize.body};
  line-height: ${t.lineHeight.body};

  li {
    margin-bottom: ${t.spacing.xs};
  }
`

export const StyledOl = styled.ol<{ $compact?: boolean }>`
  font-size: ${(props) =>
    props.$compact ? t.fontSizeCompact.body : t.fontSize.body};
  line-height: ${t.lineHeight.body};

  li {
    margin-bottom: ${t.spacing.xs};
  }
`

export const ChartContainer = styled.div<{ $compact?: boolean }>`
  /* Dense block buffer, layered on top of the wrapper's gap. */
  margin: ${t.spacing.sm} 0;
  width: 100%;
  /* Compact (sidebar/widget) mode never scrolls a chart sideways — the chart
     itself sizes to the panel width. Full-page mode keeps overflow-x: auto so
     wide charts can be scrolled into view. */
  overflow-x: ${(props) => (props.$compact ? 'hidden' : 'auto')};
  /* Explicit: without this, overflow-x: auto implicitly computes overflow-y as
     auto too (per the CSS overflow spec), so any sub-pixel overshoot inside
     the chart shows up as a spurious vertical scrollbar. The chart's own
     height is set precisely, so vertical content never needs to scroll here. */
  overflow-y: hidden;
`
